import { z } from 'zod';

// The materials list's shapes (plan 4B), used by the server and the browser.

/** The most one import takes: one unit of work, with room for its audit entry. */
export const MAX_MATERIALS_IMPORT = 90;

export const MaterialUnit = z.enum(['scu', 'units']);

export const Material = z.object({
  id: z.string(),
  name: z.string(),
  /** Hundredths of a point, per SCU or per unit. */
  value: z.number().int(),
  unit: MaterialUnit,
  hasQuality: z.boolean(),
  active: z.boolean(),
  version: z.number().int(),
});
export type Material = z.infer<typeof Material>;

const Fields = z.object({
  name: z.string().trim().min(1).max(80),
  value: z.number().int().min(0).max(1e13),
  unit: MaterialUnit.default('scu'),
  hasQuality: z.boolean().default(false),
});

const qualityInScu = (input: { unit: string; hasQuality: boolean }) => !input.hasQuality || input.unit === 'scu';
const QUALITY = { message: 'Only a material measured in SCU has quality', path: ['hasQuality'] };

export const MaterialInput = Fields.refine(qualityInScu, QUALITY);
export type MaterialInput = z.input<typeof MaterialInput>;

export const MaterialUpdate = Fields.extend({ active: z.boolean().default(true), version: z.number().int().min(1) }).refine(qualityInScu, QUALITY);
export type MaterialUpdate = z.input<typeof MaterialUpdate>;

export const ImportMaterials = z.object({ json: z.string().min(2).max(200_000) });

/** The name a unique index compares: ignoring case and outer spaces. */
export const materialKey = (name: string) => name.trim().toLowerCase();

/** FleetCom's export, `{ "Name": 12.5 }`, as names and hundredths, or what's wrong with it. */
export function materialsFromJson(json: string): [string, number][] | string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return 'That isn’t JSON';
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return 'Expected { "Name": value }';
  const rows: [string, number][] = [];
  for (const [name, value] of Object.entries(parsed)) {
    const trimmed = name.trim();
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || trimmed === '' || trimmed.length > 80) return `${name.slice(0, 40)} has no usable value`;
    rows.push([trimmed, Math.round(value * 100)]);
  }
  if (rows.length === 0) return 'There are no materials in that';
  if (rows.length > MAX_MATERIALS_IMPORT) return `That's ${String(rows.length)} materials: import at most ${String(MAX_MATERIALS_IMPORT)} at a time`;
  return rows;
}
